import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '../useI18n.js';
import { serverUrl } from '../socket';
import { MashupWheel } from './MashupWheel';

function fmtTime(s) { const m = Math.floor(s / 60); const r = Math.floor(s % 60); return `${m}:${String(r).padStart(2, '0')}`; }
function norm(s) { return String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, ''); }

// ---- Roue : le pool tourne et s'arrete sur le mashup assigne ----
function CoverWheel({ assign }) {
  const { t } = useI18n();
  const pool = assign?.pool || [];
  const [cur, setCur] = useState(0);
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!pool.length) { setDone(true); return; }
    let i = 0; let stop = false; let timer = null;
    const start = performance.now();
    const wheelMs = assign?.wheelMs || 6000;
    const tick = () => {
      if (stop) return;
      i = (i + 1) % pool.length; setCur(i);
      const el = performance.now() - start;
      if (el >= wheelMs) { setDone(true); return; }
      const delay = 45 + Math.pow(el / wheelMs, 2.2) * 300;
      timer = setTimeout(tick, delay);
    };
    timer = setTimeout(tick, 45);
    return () => { stop = true; if (timer) clearTimeout(timer); };
  }, [assign]);
  const shown = done ? assign?.landsOn : (pool[cur] || '');
  return (
    <div className="cover-wheel">
      <div className="cw-label">{done ? (t('coverYourMashup') || 'Ton mashup') : (t('coverSpinning') || 'La roue tourne...')}</div>
      <div className={`cw-name${done ? ' locked' : ''}`}>{shown || '...'}</div>
      {done && assign?.author && <div className="cw-author">{assign.author}</div>}
    </div>
  );
}

// ---- Transport audio (play/pause + seek) sur toute la piste ----
function AudioTransport({ src }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const toggle = () => { const a = ref.current; if (!a) return; if (a.paused) { a.play().then(() => setPlaying(true)).catch(() => {}); } else { a.pause(); setPlaying(false); } };
  const seek = (v) => { const a = ref.current; if (a && Number.isFinite(v)) { a.currentTime = v; setCur(v); } };
  return (
    <div className="cover-audio">
      <audio ref={ref} src={src} preload="metadata"
        onLoadedMetadata={(e) => setDur(e.target.duration || 0)}
        onTimeUpdate={(e) => setCur(e.target.currentTime || 0)}
        onEnded={() => setPlaying(false)} />
      <button type="button" className="btn ca-play" onClick={toggle}>{playing ? '⏸' : '▶'}</button>
      <input className="ca-seek" type="range" min={0} max={dur || 0} step={0.1} value={Math.min(cur, dur || 0)} onChange={(e) => seek(parseFloat(e.target.value))} />
      <span className="ca-time">{fmtTime(cur)} / {fmtTime(dur)}</span>
    </div>
  );
}

// ---- Phase dessin : canvas prive + audio + soumission PNG ----
function CoverDrawRound({ info, timeLeft, onSubmit, submitted }) {
  const { t } = useI18n();
  const canvasRef = useRef(null);
  const lastRef = useRef(null);
  const armed = useRef(false);
  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('#111827');
  const [penSize, setPenSize] = useState(5);
  const [eraserSize, setEraserSize] = useState(30);

  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const g = c.getContext('2d'); g.fillStyle = '#ffffff'; g.fillRect(0, 0, c.width, c.height);
  }, []);
  const posFrom = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (c.width / Math.max(1, r.width)), y: (e.clientY - r.top) * (c.height / Math.max(1, r.height)) };
  };
  const curSize = () => (tool === 'eraser' ? eraserSize : penSize);
  const stroke = (a, b) => {
    const g = canvasRef.current.getContext('2d');
    g.strokeStyle = tool === 'eraser' ? '#ffffff' : color;
    g.lineWidth = curSize(); g.lineCap = 'round'; g.lineJoin = 'round';
    g.beginPath(); g.moveTo(a.x, a.y); g.lineTo(b.x, b.y); g.stroke();
  };
  const onDown = (e) => { if (submitted) return; const p = posFrom(e); lastRef.current = p; try { canvasRef.current.setPointerCapture(e.pointerId); } catch {} stroke(p, p); };
  const onMove = (e) => { if (!lastRef.current) return; const p = posFrom(e); stroke(lastRef.current, p); lastRef.current = p; };
  const onUp = () => { lastRef.current = null; };
  const doSubmit = () => { if (submitted) return; try { onSubmit(canvasRef.current.toDataURL('image/png')); } catch {} };
  useEffect(() => {
    if (timeLeft > 1) armed.current = true;
    if (armed.current && !submitted && timeLeft <= 1) doSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  return (
    <div className="cover-draw">
      <div className="create-head">
        <div className="create-prompt">
          <span className="cp-label">{t('coverDrawFor') || 'Dessine la pochette de'}</span>
          <span className="cp-text">{info?.name} <span style={{ opacity: 0.7 }}>- {info?.author}</span></span>
        </div>
        {timeLeft > 0 && <span className="chrono-timer">{fmtTime(timeLeft)}</span>}
      </div>
      <AudioTransport src={info?.audioUrl ? `${serverUrl}${info.audioUrl}` : undefined} />
      <div className="draw-tools">
        <button type="button" className={`draw-tool${tool === 'pen' ? ' active' : ''}`} onClick={() => setTool('pen')} title={t('drawPen') || 'Stylo'}>✏️</button>
        <button type="button" className={`draw-tool${tool === 'eraser' ? ' active' : ''}`} onClick={() => setTool('eraser')} title={t('drawEraser') || 'Gomme'}>🧽</button>
        {tool === 'pen' && <label className="draw-color"><input type="color" value={color} onChange={(e) => setColor(e.target.value)} /></label>}
        <label className="draw-size">
          <input type="range" min={1} max={tool === 'eraser' ? 80 : 40} step={1} value={tool === 'eraser' ? eraserSize : penSize}
            onChange={(e) => (tool === 'eraser' ? setEraserSize : setPenSize)(parseInt(e.target.value, 10))} />
          <span className="draw-size-dot" style={{ width: curSize() / 2 + 4, height: curSize() / 2 + 4, background: tool === 'eraser' ? '#cbd5e1' : color }} />
        </label>
      </div>
      <canvas ref={canvasRef} width={900} height={560} className="draw-canvas cover-canvas"
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}
        style={submitted ? { pointerEvents: 'none', opacity: 0.7 } : undefined} />
      {submitted
        ? <div className="mashup-status">{t('coverSubmitted') || 'Cover envoyee, en attente des autres...'}</div>
        : <button type="button" className="btn primary create-submit" onClick={doSubmit}>{t('coverSubmit') || 'Valider ma cover'}</button>}
    </div>
  );
}

// ---- Phase reveal : image + (non-auteur) recherche + upvote / resultat ----
function CoverRevealView({ reveal, result, timeLeft, myPseudo, onGuess, onUpvote, upvote }) {
  const { t } = useI18n();
  const isAuthor = reveal?.author === myPseudo;
  const [q, setQ] = useState('');
  const [guessed, setGuessed] = useState(false);
  const [voted, setVoted] = useState(false);
  useEffect(() => { setQ(''); setGuessed(false); setVoted(false); }, [reveal?.index]);
  const pool = reveal?.searchPool || [];
  const matches = useMemo(() => {
    const n = norm(q); if (!n) return [];
    return pool.filter((name) => norm(name).includes(n)).slice(0, 8);
  }, [q, pool]);
  const pick = (name) => { setGuessed(true); setQ(name); onGuess(name); };
  const vote = () => { if (voted) return; setVoted(true); onUpvote(); };

  if (result) {
    const iWon = (result.correctGuessers || []).includes(myPseudo);
    return (
      <div className="cover-reveal">
        <div className="cr-name-reveal show">{result.name}{result.becameOfficial && <span className="cr-official">★ {t('coverOfficial') || 'Cover officielle !'}</span>}</div>
        <img className="cr-img" src={result.imageDataUrl} alt="cover" />
        <div className="cr-by">{t('coverBy') || 'Par'} {result.author} · 👍 {result.upvotes}/{result.needed}</div>
        <div className="cr-guessers">
          {(result.correctGuessers || []).length
            ? `${t('coverFound') || 'Trouve par'} : ${result.correctGuessers.join(', ')}`
            : (t('coverNobody') || 'Personne n\'a trouve')}
          {iWon && ' ✓'}
        </div>
      </div>
    );
  }

  return (
    <div className="cover-reveal">
      <div className="cr-head">
        <span className="cr-count">{reveal?.index}/{reveal?.total}</span>
        {timeLeft > 0 && <span className="chrono-timer">{timeLeft}s</span>}
      </div>
      <img className="cr-img" src={reveal?.imageDataUrl} alt="cover" />
      {isAuthor ? (
        <div className="mashup-status">{t('coverYours') || 'Ta cover ! Les autres devinent...'}</div>
      ) : guessed ? (
        <div className="mashup-status">{t('coverGuessed') || 'Reponse envoyee'} : {q}</div>
      ) : (
        <div className="cover-search">
          <input className="who-input" value={q} placeholder={t('coverGuessPlaceholder') || 'Quel mashup ? (recherche)'} onChange={(e) => setQ(e.target.value)} />
          {matches.length > 0 && (
            <div className="cover-matches">
              {matches.map((name) => <button type="button" key={name} className="cover-match" onClick={() => pick(name)}>{name}</button>)}
            </div>
          )}
        </div>
      )}
      {!isAuthor && (
        <button type="button" className={`btn cover-upvote${voted ? ' voted' : ''}`} onClick={vote} disabled={voted}>
          👍 {t('coverUpvote') || 'Belle cover'} {upvote?.needed ? `(${upvote.votes}/${upvote.needed})` : ''}
        </button>
      )}
    </div>
  );
}

export function CoverDrawGame({ phase, assign, draw, reveal, result, upvote, timeLeft, myPseudo, onSubmitCover, onGuess, onUpvote }) {
  const [submitted, setSubmitted] = useState(false);
  useEffect(() => { setSubmitted(false); }, [draw?.name]);
  if (phase === 'wheel') return <MashupWheel assign={assign} />;
  if (phase === 'drawing') {
    const submit = (url) => { setSubmitted(true); onSubmitCover(url); };
    return <CoverDrawRound info={draw} timeLeft={timeLeft} onSubmit={submit} submitted={submitted} />;
  }
  if (phase === 'reveal') return <CoverRevealView reveal={reveal} result={result} timeLeft={timeLeft} myPseudo={myPseudo} onGuess={onGuess} onUpvote={onUpvote} upvote={upvote} />;
  return <div className="cover-draw"><div className="mashup-status">...</div></div>;
}
